import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (!isLanding) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { label: "Overview", id: "overview" },
    { label: "Risk", id: "risk" },
    { label: "Divisions", id: "divisions" },
    { label: "Trust", id: "trust" },
    { label: "Attestation", id: "attestation", route: "/attestation" },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#050B14]/90 backdrop-blur-xl border-b border-slate-800/50"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          {/* Logo */}
          <button
            data-testid="header-logo"
            onClick={() => {
              if (!isLanding) navigate("/");
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 group"
          >
            <span className="font-serif text-xl font-bold text-white tracking-tight">
              JCAM
            </span>
            <span className="hidden sm:block w-px h-5 bg-slate-700" />
            <span className="hidden sm:block text-xs tracking-[0.2em] uppercase text-slate-400 font-sans">
              Asset Management
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => item.route ? navigate(item.route) : scrollTo(item.id)}
                className="text-sm text-slate-400 hover:text-white transition-colors duration-300 tracking-wide"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            {/* Desktop CTA */}
            <button
              data-testid="header-contact-cta"
              onClick={() => scrollTo("contact")}
              className="hidden md:flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300"
            >
              Contact
              <ArrowRight className="w-3.5 h-3.5" />
            </button>

            {/* Mobile Hamburger */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden text-slate-300 hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        data-testid="mobile-menu-overlay"
        className={`fixed inset-0 z-40 bg-[#050B14]/98 backdrop-blur-xl transition-all duration-500 md:hidden flex flex-col items-center justify-center ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`mobile-nav-${item.id}`}
              onClick={() => { setMobileOpen(false); item.route ? navigate(item.route) : scrollTo(item.id); }}
              className="text-2xl font-serif text-slate-300 hover:text-white transition-colors duration-300 tracking-wide"
            >
              {item.label}
            </button>
          ))}
          <div className="w-12 h-px bg-slate-700 my-2" />
          <button
            data-testid="mobile-contact-cta"
            onClick={() => scrollTo("contact")}
            className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300"
          >
            Contact Us
          </button>
        </nav>
      </div>
    </>
  );
}
